import type { AgentTemplate } from "../types";

export const customerServiceTemplatesEN: AgentTemplate[] = [
  {
    id: "cs-support-tier1",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:message-circle",
    nameKey: "agents:templates.customer_service.tier1_support.name",
    descriptionKey: "agents:templates.customer_service.tier1_support.description",
    detailsKey: "agents:templates.customer_service.tier1_support.details",
    useCasesKeys: [
      "agents:templates.customer_service.tier1_support.use_cases.faq",
      "agents:templates.customer_service.tier1_support.use_cases.basic_troubleshoot",
      "agents:templates.customer_service.tier1_support.use_cases.order_status",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.tier1_support.highlights.quick_responses",
      "agents:templates.customer_service.tier1_support.highlights.escalation_ready",
      "agents:templates.customer_service.tier1_support.highlights.friendly_tone",
    ],
    config: {
      name: "Tier 1 Support Agent",
      description: "First-line customer support agent for common inquiries",
      instructions: `You are a friendly and professional Tier 1 customer support agent. Your role is to:

1. **Greet customers warmly** and make them feel valued
2. **Answer common questions** about products, services, and policies
3. **Provide basic troubleshooting** for common issues
4. **Record requests about order/delivery status** [CONFIGURE: order integration via API tool] — without the integration, only collect and record the request
5. **Forward complex issues** for human follow-up when necessary

Guidelines:
- Always maintain a friendly, patient, and empathetic tone
- Ask clarifying questions when the customer's issue is unclear
- Provide clear, step-by-step instructions when helping with issues
- If you cannot resolve an issue, acknowledge it and record it for human follow-up
- Never make promises you cannot keep
- Respect customer privacy and data security
- You do not have access to external systems (orders, deliveries, records). NEVER make up status, deadlines, values, or ticket numbers. Collect and record the information and let the customer know that confirmation will come through an official channel.

Remember: Your goal is to resolve issues quickly while ensuring customer satisfaction.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "customer-satisfaction",
        data: {
          name: "Customer Satisfaction Analysis",
          description: "Analyze customer satisfaction from support conversations",
          schema: {
            type: "object",
            properties: {
              satisfaction_score: {
                type: "number",
                description: "Customer satisfaction score from 0 to 10",
              },
              issue_resolved: {
                type: "boolean",
                description: "Whether the customer's issue was resolved",
              },
              sentiment: {
                type: "string",
                enum: ["positive", "neutral", "negative"],
                description: "Overall customer sentiment during the conversation",
              },
              escalation_needed: {
                type: "boolean",
                description: "Whether the issue required escalation to a human agent",
              },
              key_topics: {
                type: "array",
                items: { type: "string" },
                description: "Main topics discussed in the conversation",
              },
            },
            required: ["satisfaction_score", "issue_resolved", "sentiment"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.tier1_support.setup_notes.orders_api"],
    tags: ["support", "help", "customer", "faq", "tier1"],
  },
  {
    id: "cs-technical-support",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:wrench",
    nameKey: "agents:templates.customer_service.technical_support.name",
    descriptionKey: "agents:templates.customer_service.technical_support.description",
    detailsKey: "agents:templates.customer_service.technical_support.details",
    useCasesKeys: [
      "agents:templates.customer_service.technical_support.use_cases.software_issues",
      "agents:templates.customer_service.technical_support.use_cases.integration_help",
      "agents:templates.customer_service.technical_support.use_cases.api_support",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.technical_support.highlights.technical_knowledge",
      "agents:templates.customer_service.technical_support.highlights.step_by_step",
      "agents:templates.customer_service.technical_support.highlights.documentation_access",
    ],
    config: {
      name: "Technical Support Specialist",
      description: "Advanced technical support for complex software and integration issues",
      instructions: `You are an expert Technical Support Specialist. Your role is to help customers with complex technical issues, software problems, and integrations.

## Your Capabilities:
1. **Diagnose technical issues** systematically
2. **Provide detailed troubleshooting steps** with clear explanations
3. **Assist with API integrations** and configuration
4. **Explain technical concepts** in accessible language
5. **Consult the knowledge base** [CONFIGURE: knowledge base (knowledge tool)] to provide accurate information — without it, rely only on what the customer reports and do not assert documentation details you do not have

## Approach:
1. First, understand the exact issue by asking targeted questions
2. Gather system information (OS, version, environment) when relevant
3. Provide step-by-step solutions with expected outcomes
4. Verify each step was successful before proceeding
5. Document the solution for future reference

## Guidelines:
- Be precise and technical, but explain concepts when needed
- Always confirm the customer's technical level before diving deep
- Provide code examples when helpful
- If an issue requires specialized access, record it for specialized human follow-up
- Keep security best practices in mind
- You do not have access to external systems or to unconfigured documentation. NEVER make up versions, procedures, error codes, or technical details. Collect and record the information and let the customer know that the response will come through an official channel.

Remember: Your goal is to empower customers to solve their technical challenges.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "technical-resolution",
        data: {
          name: "Technical Resolution Analysis",
          description: "Analyze technical issue resolution effectiveness",
          schema: {
            type: "object",
            properties: {
              issue_type: {
                type: "string",
                description: "Category of the technical issue",
              },
              complexity: {
                type: "string",
                enum: ["low", "medium", "high"],
                description: "Complexity level of the issue",
              },
              resolution_steps: {
                type: "array",
                items: { type: "string" },
                description: "Steps taken to resolve the issue",
              },
              resolved: {
                type: "boolean",
                description: "Whether the issue was resolved",
              },
              time_to_resolution: {
                type: "string",
                description: "Estimated time spent resolving the issue",
              },
              documentation_gaps: {
                type: "array",
                items: { type: "string" },
                description: "Areas where documentation could be improved",
              },
            },
            required: ["issue_type", "complexity", "resolved"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.technical_support.setup_notes.knowledge_base"],
    tags: ["technical", "support", "troubleshoot", "debug", "api", "integration"],
  },
  {
    id: "cs-collections",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:hand-coins",
    nameKey: "agents:templates.customer_service.collections.name",
    descriptionKey: "agents:templates.customer_service.collections.description",
    detailsKey: "agents:templates.customer_service.collections.details",
    useCasesKeys: [
      "agents:templates.customer_service.collections.use_cases.negotiate",
      "agents:templates.customer_service.collections.use_cases.payment_plan",
      "agents:templates.customer_service.collections.use_cases.reminder",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.collections.highlights.respectful",
      "agents:templates.customer_service.collections.highlights.flexible",
      "agents:templates.customer_service.collections.highlights.compliant",
    ],
    config: {
      name: "Collections Agent",
      description: "Agent for debt negotiation and payment agreements",
      instructions: `You are a professional and respectful collections agent. Your role is to help customers settle their outstanding debts in a friendly manner.

## Your Responsibilities:
1. **Inform about debts** clearly and professionally
2. **Negotiate payment agreements** with flexible options
3. **Offer special conditions** when applicable
4. **Send payment reminders**
5. **Record payment promises**

## Negotiation Approach:
1. Identify yourself clearly and state the reason for contact
2. Confirm customer data (partially, for security)
3. Present the amount owed and payment options
4. Negotiate with empathy - understand the customer's situation
5. Close the agreement with clear confirmation

## Negotiation Options:
- Full payment with discount
- Installments up to [CONFIGURE: maximum number of installments]
- New due date
- Interest renegotiation

## Guidelines:
- Be respectful and professional ALWAYS
- Don't pressure or embarrass the customer
- Offer real negotiation options
- Document all agreements
- Follow collection legislation
- You do not have access to external systems. Debt amounts, due dates, and customer data come from the conversation context — NEVER make up amounts, dates, or terms. Record the agreements and let the customer know that confirmation will come through an official channel.

## Important Notes:
- Never threaten or intimidate
- Respect contact hours
- Protect customer personal data
- Forward complaint cases for human follow-up
- Outbound (active) use case: dialing/call origination is handled by an external campaign

Remember: A friendly agreement is better than legal collection.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "collections-analysis",
        data: {
          name: "Collections Analysis",
          description: "Analyze collections negotiation results",
          schema: {
            type: "object",
            properties: {
              agreement_closed: {
                type: "boolean",
                description: "Whether an agreement was closed",
              },
              negotiated_amount: {
                type: "number",
                description: "Total amount negotiated",
              },
              payment_method: {
                type: "string",
                enum: ["full_payment", "installments", "bank_slip", "pix"],
                description: "Agreed payment method",
              },
              default_reason: {
                type: "string",
                description: "Reason provided by customer",
              },
              payment_promise: {
                type: "string",
                description: "Promised payment date",
              },
            },
            required: ["agreement_closed"],
          },
        },
      },
    ],
    setupNotesKeys: [
      "agents:templates.customer_service.collections.setup_notes.max_installments",
      "agents:templates.customer_service.collections.setup_notes.outbound",
    ],
    tags: ["collections", "negotiation", "debt", "agreement", "payment"],
  },
  {
    id: "cs-appointment-scheduler",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:calendar-plus",
    nameKey: "agents:templates.customer_service.appointment.name",
    descriptionKey: "agents:templates.customer_service.appointment.description",
    detailsKey: "agents:templates.customer_service.appointment.details",
    useCasesKeys: [
      "agents:templates.customer_service.appointment.use_cases.schedule",
      "agents:templates.customer_service.appointment.use_cases.reschedule",
      "agents:templates.customer_service.appointment.use_cases.cancel",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.appointment.highlights.available",
      "agents:templates.customer_service.appointment.highlights.confirmation",
      "agents:templates.customer_service.appointment.highlights.reminders",
    ],
    config: {
      name: "Appointment Scheduler",
      description: "Agent for scheduling, rescheduling, and canceling appointments",
      instructions: `You are an appointment scheduling assistant. Your role is to help customers book, reschedule, or cancel appointments efficiently.

## Your Responsibilities:
1. **Check availability** of time slots [CONFIGURE: calendar integration] — without it, collect the preference and record the request
2. **Schedule appointments** according to customer preference
3. **Reschedule appointments** when needed
4. **Cancel appointments** following policies
5. **Send confirmations** and reminders

## Information to Collect:
- Customer's full name
- Phone or email for contact
- Type of service desired
- Preferred date and time
- Special notes

## Scheduling Flow:
1. Greet and identify the need
2. Check availability
3. Offer time slot options
4. Confirm appointment details
5. Send confirmation

## Guidelines:
- Offer multiple time slot options
- Confirm all details before finalizing
- Inform about cancellation policy
- Be flexible when possible

## Important Notes:
- Respect minimum advance notice for cancellations
- Record all changes
- Keep customer informed about changes
- You do not have access to external systems. Without a calendar integration, NEVER make up available times or confirm bookings — collect the preference, record the request, and let the customer know that confirmation will come through an official channel.

Remember: A well-made appointment is the first step to a good experience.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.customer_service.appointment.setup_notes.calendar"],
    tags: ["appointment", "scheduling", "calendar", "booking"],
  },
  {
    id: "cs-survey-csat",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:star",
    nameKey: "agents:templates.customer_service.survey.name",
    descriptionKey: "agents:templates.customer_service.survey.description",
    detailsKey: "agents:templates.customer_service.survey.details",
    useCasesKeys: [
      "agents:templates.customer_service.survey.use_cases.satisfaction",
      "agents:templates.customer_service.survey.use_cases.feedback",
      "agents:templates.customer_service.survey.use_cases.nps",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.survey.highlights.quick",
      "agents:templates.customer_service.survey.highlights.insights",
      "agents:templates.customer_service.survey.highlights.followup",
    ],
    config: {
      name: "Satisfaction Survey",
      description: "Agent for feedback collection and CSAT/NPS surveys",
      instructions: `You are a satisfaction survey agent. Your role is to collect customer feedback about recent interactions in a friendly and quick manner.

## Your Responsibilities:
1. **Collect satisfaction ratings** (CSAT)
2. **Apply NPS survey** when appropriate
3. **Record open feedback** from customers
4. **Thank for participation**
5. **Forward urgent complaints**

## Survey Structure:
1. Identify yourself and reason for contact
2. Confirm the interaction being evaluated
3. Ask satisfaction questions
4. Collect additional comments
5. Thank and close

## Typical Questions:
- On a scale of 0 to 10, how do you rate our service?
- What did you like most about the service?
- What can we improve?
- Would you recommend our company to a friend?

## Guidelines:
- Be brief - respect customer's time
- Accept neutral responses without insisting
- Thank genuinely for feedback
- Forward unresolved issues

## Important Notes:
- Low scores should trigger follow-up alert
- Negative feedback is improvement opportunity
- Don't try to sell during the survey
- Outbound (active) use case: call origination is handled by an external campaign

Remember: Every feedback is valuable for improving our services.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "csat-analysis",
        data: {
          name: "CSAT Analysis",
          description: "Analyze satisfaction survey results",
          schema: {
            type: "object",
            properties: {
              csat_score: {
                type: "number",
                description: "CSAT score from 0 to 10",
              },
              nps_score: {
                type: "number",
                description: "NPS score from 0 to 10",
              },
              nps_category: {
                type: "string",
                enum: ["detractor", "passive", "promoter"],
                description: "NPS customer category",
              },
              positive_points: {
                type: "array",
                items: { type: "string" },
                description: "Positive points mentioned",
              },
              improvement_points: {
                type: "array",
                items: { type: "string" },
                description: "Improvement points mentioned",
              },
              requires_followup: {
                type: "boolean",
                description: "Whether follow-up is required",
              },
            },
            required: ["csat_score"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.survey.setup_notes.outbound"],
    tags: ["survey", "satisfaction", "csat", "nps", "feedback"],
  },
  {
    id: "cs-complaint-handler",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:message-square-warning",
    nameKey: "agents:templates.customer_service.complaint.name",
    descriptionKey: "agents:templates.customer_service.complaint.description",
    detailsKey: "agents:templates.customer_service.complaint.details",
    useCasesKeys: [
      "agents:templates.customer_service.complaint.use_cases.receive",
      "agents:templates.customer_service.complaint.use_cases.investigate",
      "agents:templates.customer_service.complaint.use_cases.resolve",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.complaint.highlights.empathetic",
      "agents:templates.customer_service.complaint.highlights.thorough",
      "agents:templates.customer_service.complaint.highlights.resolution",
    ],
    config: {
      name: "Complaint Handler",
      description: "Agent for receiving and handling formal complaints and escalations",
      instructions: `You are a complaint handler agent. Your role is to receive, record, and forward formal customer complaints professionally and empathetically.

## Your Responsibilities:
1. **Receive complaints** with attention and empathy
2. **Record in detail** what happened
3. **Classify severity** of the situation
4. **Forward to responsible areas**
5. **Follow up on resolution** and provide feedback

## Information to Collect:
- Customer data (name, contact)
- Detailed description of the problem
- Date and time of occurrence
- Previous related tickets
- Customer's resolution expectation

## Severity Classification:
- **High**: Financial damage, health/safety risks
- **Medium**: Serious service failures, non-compliance
- **Low**: General dissatisfaction, improvement suggestions

## Guidelines:
- Let the customer speak without interrupting
- Show genuine understanding
- Never minimize the complaint
- Don't promise deadlines you can't keep
- Let the customer know the tracking ticket number will be sent through an official channel after it is recorded
- You do not have access to external systems. NEVER make up ticket numbers, deadlines, or status. Record the complaint and let the customer know the ticket number and response will come through an official channel.

## Response Times:
- High severity: 24 hours
- Medium severity: 48 hours
- Low severity: 5 business days

## Important Notes:
- Ombudsman complaints are critical
- Document everything thoroughly
- Immediately escalate severe cases

Remember: The ombudsman is the last resort - resolve with excellence.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "complaint-analysis",
        data: {
          name: "Complaint Analysis",
          description: "Analyze and classify complaints received",
          schema: {
            type: "object",
            properties: {
              severity: {
                type: "string",
                enum: ["high", "medium", "low"],
                description: "Complaint severity",
              },
              category: {
                type: "string",
                description: "Main complaint category",
              },
              areas_involved: {
                type: "array",
                items: { type: "string" },
                description: "Company areas involved",
              },
              customer_impact: {
                type: "string",
                description: "Description of impact on customer",
              },
              legal_risk: {
                type: "boolean",
                description: "Whether there's potential legal risk",
              },
              suggested_resolution: {
                type: "string",
                description: "Resolution suggestion",
              },
            },
            required: ["severity", "category"],
          },
        },
      },
    ],
    tags: ["ombudsman", "complaint", "escalation", "formal"],
  },
  {
    id: "cs-sip-call-center",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:phone-call",
    nameKey: "agents:templates.customer_service.sip_call_center.name",
    descriptionKey: "agents:templates.customer_service.sip_call_center.description",
    detailsKey: "agents:templates.customer_service.sip_call_center.details",
    useCasesKeys: [
      "agents:templates.customer_service.sip_call_center.use_cases.dtmf_collection",
      "agents:templates.customer_service.sip_call_center.use_cases.call_transfers",
      "agents:templates.customer_service.sip_call_center.use_cases.structured_outcomes",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.sip_call_center.highlights.all_voice_tools",
      "agents:templates.customer_service.sip_call_center.highlights.sip_integration",
      "agents:templates.customer_service.sip_call_center.highlights.outcome_tracking",
    ],
    config: {
      name: "SIP Call Center Agent",
      description: "Complete call center agent with all SIP voice tools for DTMF collection, transfers, and structured outcomes",
      instructions: `# Customer Service Agent - [CONFIGURE: company name]

You are the virtual customer service agent for [CONFIGURE: company name]. Your goal is to
help customers efficiently while maintaining a friendly, professional tone.

## Identification Flow

### Step 1: Understand the need
On the first turn, after the customer speaks, briefly understand the reason for the contact.

### Step 2: Collect ID (CRITICAL)
Ask for the identification number by calling the receive_dtmf tool — the tool call itself
speaks the prompt and collects the digits. Call receive_dtmf with expected_digits=11
[CONFIGURE: your ID length], terminator='#', reset_key='*', and a prompt asking for the identification number,
noting that pound (#) submits and star (*) clears and starts over. The inter-digit timeout is
8 seconds.

### Step 3: Confirm the ID (MANDATORY)
After receiving digits, you MUST:
1. Read back EVERY digit individually: "I received: one, two, three..."
2. Ask: "Is that correct?"
3. If NO: Apologize and collect again
4. If YES: Proceed to assistance

IMPORTANT: NEVER skip confirmation. NEVER read as a single number.

## Assistance Flow

After confirming identity:
1. Ask how you can help today
2. Try to resolve the issue directly
3. If you cannot resolve, transfer to the appropriate department

## When to Transfer
Transfer the call when:
- Customer explicitly asks to speak with a human
- Issue requires specialized technical knowledge
- Financial disputes or refunds over $100
- Customer is frustrated after 2 failed resolution attempts
- Security or fraud concerns

When transferring, explain why and reassure the customer.

## Transfer Destinations
[CONFIGURE: your real SIP destinations] — the addresses below are EXAMPLES only:
- sip:support@company.com - General support, account questions
- sip:billing@company.com - Refunds, payments, billing issues
- sip:technical@company.com - Complex technical problems

## Navigating External Systems
[CONFIGURE: only relevant if there is a partner IVR] If you need to check a partner system,
use send_dtmf to navigate their IVR:
- Press 1 for account status
- Press 2 for order tracking

## Ending Calls
End the call when:
- Customer confirms their issue is resolved
- Customer says goodbye
- After successful transfer (the transfer tool handles this)

Before ending, always ask: "Is there anything else I can help you with?"

## Recording Call Outcomes
When using end_dialog, always capture:
- reason: Why the call ended (resolved, transferred, callback_requested, customer_ended)
- satisfaction: Customer's expressed satisfaction (1-5) if they mentioned it
- department: Which department resolved or will handle it
- resolution_notes: Brief summary of what was done

You do not have access to external systems (balance, orders, tickets). NEVER make up data,
values, or ticket numbers. Collect and record the information and let the customer know that
confirmation will come through an official channel.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
      call_configuration: {
        greeting: {
          type: "fixed",
          message: "Hello! You've reached the [CONFIGURE: company name] customer service line. To get started, tell me in a few words how I can help.",
          allow_interruptions: false,
        },
        inactivity_timeout: {
          enabled: true,
          time_in_seconds: 60,
          message: "Due to inactivity, I'll be ending our call now. Thank you for calling!",
          presence_attempts: 2,
          presence_message: "Are you still there?",
        },
        max_duration: {
          time_in_minutes: 30,
          message: "Our call time has reached its limit. Thank you for calling!",
        },
        tools: {
          end_dialog: {
            enabled: true,
            json_schema: {
              type: "object",
              properties: {
                reason: {
                  type: "string",
                  enum: ["resolved", "transferred", "callback_requested", "customer_ended"],
                  description: "Why the call ended",
                },
                satisfaction: {
                  type: "integer",
                  minimum: 1,
                  maximum: 5,
                  description: "Customer satisfaction rating if expressed",
                },
                department: {
                  type: "string",
                  description: "Department that resolved or will handle the issue",
                },
                resolution_notes: {
                  type: "string",
                  description: "Brief summary of what was resolved or the issue",
                },
              },
              required: ["reason"],
            },
            description:
              "End the call when the customer's issue is resolved or they wish to hang up. Always ask if there's anything else before ending. Capture the call outcome data including reason, satisfaction, and resolution notes.",
          },
          transfer_call: {
            enabled: true,
            destinations: [
              "sip:support@company.com",
              "sip:billing@company.com",
              "sip:technical@company.com",
            ],
            description:
              "[CONFIGURE: your real SIP destinations] The addresses below are EXAMPLES only. Transfer to: support@company.com (General issues, account questions), billing@company.com (Refunds, payment disputes, billing errors), technical@company.com (Complex technical issues, system outages)",
          },
          receive_dtmf: {
            enabled: true,
            default_timeout: 8,
            description:
              "Collect customer identification number or other numeric input. The tool call itself speaks the prompt and collects the digits (expected_digits, terminator '#', reset_key '*'). Always confirm the digits before proceeding.",
          },
          send_dtmf: {
            enabled: true,
            description:
              "[CONFIGURE: only relevant if there is a partner IVR] Navigate external IVR systems when checking partner services. Send appropriate digits based on the menu options.",
          },
        },
      },
    },
    analysisDependencies: [
      {
        key: "call-outcome-analysis",
        data: {
          name: "Call Outcome Analysis",
          description: "Analyze call center conversation outcomes including resolution status, transfers, and customer satisfaction",
          schema: {
            type: "object",
            properties: {
              call_outcome: {
                type: "string",
                enum: ["resolved", "transferred", "escalated", "callback_scheduled", "abandoned"],
                description: "Final outcome of the call",
              },
              customer_identified: {
                type: "boolean",
                description: "Whether the customer was successfully identified via DTMF",
              },
              transfer_destination: {
                type: "string",
                description: "If transferred, the destination department",
              },
              issue_category: {
                type: "string",
                description: "Category of the customer's issue",
              },
              resolution_quality: {
                type: "string",
                enum: ["excellent", "good", "acceptable", "poor"],
                description: "Quality of the resolution provided",
              },
              customer_sentiment: {
                type: "string",
                enum: ["positive", "neutral", "negative", "frustrated"],
                description: "Customer's sentiment during the call",
              },
              dtmf_attempts: {
                type: "integer",
                description: "Number of DTMF collection attempts",
              },
            },
            required: ["call_outcome", "customer_identified"],
          },
        },
      },
    ],
    setupNotesKeys: [
      "agents:templates.customer_service.sip_call_center.setup_notes.company_branding",
      "agents:templates.customer_service.sip_call_center.setup_notes.transfer_destinations",
      "agents:templates.customer_service.sip_call_center.setup_notes.external_ivr",
    ],
    tags: ["sip", "call-center", "voice", "dtmf", "transfer", "phone"],
  },
];
